import { createRoomShellReferenceRender } from '@/lib/world/background-reference';
import useWorldStore from '../store/worldStore';
import { getCanonicalActivityId } from '../simulation/activityConfig';

const API_BASE = '';

export const ACTIVITY_PROMPTS = {
  sleeping: {
    prompt: 'cyberpunk apartment bedroom, neon glow through window blinds, dark moody',
    walkableShape: 'square',
    room: 'bedroom',
  },
  eating: {
    prompt: 'dark cyberpunk ramen bar, steam rising, neon signs overhead, wet countertop',
    walkableShape: 'square',
    room: 'dining',
  },
  gym: {
    prompt: 'underground gym, industrial lighting, exposed concrete, neon trim on equipment',
    walkableShape: 'square',
    room: 'gym',
  },
  grocery: {
    prompt: 'cyberpunk convenience store interior, late night, reflective wet floor, packed shelves',
    walkableShape: 'square',
    room: 'store',
  },
  watching_tv: {
    prompt: 'cyberpunk apartment living room, large TV glow, city lights through rain-streaked window',
    walkableShape: 'square',
    room: 'living_room',
  },
  hanging_out: {
    prompt: 'neon-lit rooftop bar, night megacity skyline, string lights, light rain',
    walkableShape: 'circle',
    room: 'bar',
  },
  commuting: {
    prompt: 'cyberpunk metro station platform, train headlights in dark tunnel, wet platform',
    walkableShape: 'triangle',
    room: 'station',
  },
  reading: {
    prompt: 'small cyberpunk cafe corner, warm amber lamp, rain on window, wooden table',
    walkableShape: 'triangle',
    room: 'cafe',
  },
  working: {
    prompt: 'cyberpunk open-plan office, holographic screens, panoramic night city view',
    walkableShape: 'square',
    room: 'office',
  },
  // Life sim engine activities → rooms
  sleep: {
    prompt: 'cyberpunk apartment bedroom, neon glow through window blinds, dark moody, unmade bed',
    walkableShape: 'square',
    room: 'bedroom',
  },
  morning_routine: {
    prompt: 'cyberpunk apartment bathroom, mirror with neon edge lighting, wet tiles, steam',
    walkableShape: 'square',
    room: 'bathroom',
  },
  exercise: {
    prompt: 'underground gym, industrial lighting, exposed concrete, neon trim on equipment',
    walkableShape: 'square',
    room: 'gym',
  },
  social: {
    prompt: 'neon-lit rooftop bar, night megacity skyline, string lights, light rain',
    walkableShape: 'circle',
    room: 'bar',
  },
  creative: {
    prompt: 'cyberpunk studio apartment, art supplies, holographic displays, moody neon lighting',
    walkableShape: 'square',
    room: 'studio',
  },
  gaming: {
    prompt: 'cyberpunk apartment living room, multiple screens glowing, RGB lighting, dark',
    walkableShape: 'square',
    room: 'living_room',
  },
  music: {
    prompt: 'cyberpunk apartment living room, large TV glow, city lights through rain-streaked window',
    walkableShape: 'square',
    room: 'living_room',
  },
  chores: {
    prompt: 'cyberpunk apartment kitchen, neon under-cabinet lights, clean counters, city view',
    walkableShape: 'square',
    room: 'kitchen',
  },
  nap: {
    prompt: 'cyberpunk apartment bedroom, neon glow through window blinds, dark moody, unmade bed',
    walkableShape: 'square',
    room: 'bedroom',
  },
  work: {
    prompt: 'cyberpunk open-plan office, holographic screens, panoramic night city view',
    walkableShape: 'square',
    room: 'office',
  },
  day_trip: {
    prompt: 'cyberpunk park overlook, elevated boardwalk, neon city skyline in the distance, breezy afternoon',
    walkableShape: 'circle',
    room: 'park',
  },
  brunch: {
    prompt: 'cyberpunk brunch cafe, sun through big windows, colorful dishes, neon reflections on polished tables',
    walkableShape: 'square',
    room: 'dining',
  },
  outdoor: {
    prompt: 'cyberpunk urban park path, glowing signs, landscaped greenery, distant transit lines',
    walkableShape: 'circle',
    room: 'park',
  },
  sleep_in: {
    prompt: 'cyberpunk apartment bedroom, soft morning light, neon leaking through curtains, cozy blankets',
    walkableShape: 'square',
    room: 'bedroom',
  },
};

export function getSceneConfig(activityType) {
  const direct = ACTIVITY_PROMPTS[activityType];
  if (direct) return direct;
  const canonicalType = getCanonicalActivityId(activityType);
  if (canonicalType && canonicalType !== activityType) {
    return ACTIVITY_PROMPTS[canonicalType] || null;
  }
  return null;
}

// Map activity to a location-scoped scene key
// Same room at same district = same scene (e.g. all bedroom scenes at residential_north share one image)
export function getSceneKey(activityType, districtId) {
  const config = getSceneConfig(activityType);
  const room = config?.room || activityType;
  return `${districtId}_${room}`;
}

const LEGACY_SCENE_STORAGE_KEYS = ['ego_world_scenes'];
const SCENE_STORAGE_KEY = 'ego_world_scenes_v2';
const inflightScenes = new Map();

// Load persisted scenes from localStorage into store on init
export function loadPersistedScenes() {
  try {
    LEGACY_SCENE_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
    const raw = localStorage.getItem(SCENE_STORAGE_KEY);
    if (!raw) return;
    const saved = JSON.parse(raw);
    const store = useWorldStore.getState();
    for (const [key, scene] of Object.entries(saved)) {
      if (scene.status === 'ready' && scene.directUrl) {
        store.setSceneReady(key, scene);
      }
    }
  } catch {}
}

function persistScene(locationTag, scene) {
  try {
    const raw = localStorage.getItem(SCENE_STORAGE_KEY);
    const saved = raw ? JSON.parse(raw) : {};
    saved[locationTag] = { id: scene.id, status: 'ready', directUrl: scene.directUrl };
    localStorage.setItem(SCENE_STORAGE_KEY, JSON.stringify(saved));
  } catch {}
}

function removePersistedScene(locationTag) {
  try {
    const raw = localStorage.getItem(SCENE_STORAGE_KEY);
    if (!raw) return;
    const saved = JSON.parse(raw);
    delete saved[locationTag];
    localStorage.setItem(SCENE_STORAGE_KEY, JSON.stringify(saved));
  } catch {}
}

// Circuit breaker: once the server fails, stop retrying for 5 minutes
let serverOffline = false;
let offlineUntil = 0;

function isServerOffline() {
  if (!serverOffline) return false;
  if (Date.now() > offlineUntil) {
    serverOffline = false;
    return false;
  }
  return true;
}

function markServerOffline() {
  if (!serverOffline) {
    console.warn('Scene generation server not available — pausing requests for 5 minutes');
  }
  serverOffline = true;
  offlineUntil = Date.now() + 5 * 60 * 1000;
}

function markSceneUnavailable(locationTag, reason = 'unavailable') {
  useWorldStore.getState().setSceneUnavailable(locationTag, reason);
}

export async function generateScene(activityType, locationTag, options = {}) {
  const { force = false } = options;

  if (!force && isServerOffline()) {
    markSceneUnavailable(locationTag, 'server-offline');
    return null;
  }

  if (force) {
    serverOffline = false;
    offlineUntil = 0;
  }

  const store = useWorldStore.getState();
  const cached = store.locationScenes[locationTag];

  if (!force && cached?.status === 'ready') return cached;
  const existing = inflightScenes.get(locationTag);
  if (existing) return existing;
  if (!force && cached?.status === 'generating') return cached;

  const config = getSceneConfig(activityType);
  if (!config) return null;

  const request = (async () => {
    try {
      store.setSceneGenerating(locationTag, locationTag);
      const referenceRender = await createRoomShellReferenceRender(config.walkableShape);
      const res = await fetch(`${API_BASE}/api/world/backgrounds/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sceneKey: locationTag,
          prompt: config.prompt,
          walkableShape: config.walkableShape,
          referenceRender,
        }),
      });

      if (!res.ok) {
        markServerOffline();
        markSceneUnavailable(locationTag, 'generation-failed');
        return null;
      }

      const data = await res.json();
      if (data.backgroundUrl) {
        const scene = {
          id: data.sceneKey || locationTag,
          status: 'ready',
          directUrl: data.backgroundUrl,
          referenceUrl: data.referenceUrl || null,
          referenceSha256: data.referenceSha256 || null,
        };
        store.setSceneReady(locationTag, scene);
        persistScene(locationTag, scene);
        return scene;
      }

      markServerOffline();
      markSceneUnavailable(locationTag, 'generation-failed');
      return null;
    } catch {
      markServerOffline();
      markSceneUnavailable(locationTag, 'generation-failed');
      return null;
    } finally {
      inflightScenes.delete(locationTag);
    }
  })();

  inflightScenes.set(locationTag, request);
  return request;
}

export async function clearSceneBackground(locationTag) {
  if (!locationTag) return false;

  removePersistedScene(locationTag);
  inflightScenes.delete(locationTag);
  useWorldStore.getState().clearScene(locationTag);

  try {
    const response = await fetch(
      `${API_BASE}/api/world/backgrounds/${encodeURIComponent(locationTag)}`,
      { method: 'DELETE' }
    );
    const payload = await response.json().catch(() => ({ deleted: false }));
    return Boolean(payload?.deleted);
  } catch {
    return false;
  }
}

export async function regenerateScene(activityType, locationTag) {
  await clearSceneBackground(locationTag);
  return generateScene(activityType, locationTag, { force: true });
}

export function getSceneUrls(id) {
  return {
    background: `${API_BASE}/generated/sets/${id}/background.png`,
    depthMask: `${API_BASE}/generated/sets/${id}/depth-mask.png`,
  };
}
